<template>
    <StoreBreadcrumb :items="breadcrumb" />
    <UPage>
      <UPageHeader :title="pageTitle" :description="catalogDescription" :headline="pageHeadline" />

      <template #left>
        <UPageAside>
          <ActiveFilters @clear="catalog.reset" class="mb-4" v-model:filters="catalog.filter.value" :catalogMinPrice.number="catalogMinPrice" :catalogMaxPrice.number="catalogMaxPrice" />
          <FilterSidebar :categories="tree" v-model:filter="catalog.filter.value" :hide-category="true" :catalogMinPrice="0" :catalogMaxPrice.number="catalogMaxPrice" />
        </UPageAside>
      </template>

      <UPageBody>
        <div class="flex items-center justify-between ">
          <ViewToggle :view="catalog.view.value" @update:view="catalog.view.value = $event" />

          <USlideover side="left" title="Catalog filter" @update:open="mobileFilters = catalog.filter.value">
            <UButton icon="i-heroicons-adjustments-horizontal" class="lg:hidden" label="Filter" color="primary"
              variant="solid" />

            <template #body>
              <FilterSidebar :categories="tree" v-model:filter="mobileFilters" :hide-category="true" :catalogMinPrice="0" :catalogMaxPrice.number="catalogMaxPrice" />
            </template>

            <template #footer="{ close }">
              <div class="w-full flex justify-between gap-4">
                <UButton label="Reset Filters" color="primary" variant="link" size="lg"
                  @click="() => { catalog.reset(); close() }" />
                <UButton label="Apply Filters" color="primary" variant="solid" size="lg"
                  @click="() => { catalog.filter.value = mobileFilters; close() }" />
              </div>
            </template>
          </USlideover>

          <div class="flex items-center gap-3">
            <label class="hidden md:block text-sm font-semibold text-gray-700">{{ $t('Sort by') }}:</label>
            <SortSelect v-model="catalog.sortBy.value" class="w-48" />
          </div>
        </div>

        <USeparator />

        <!-- Loading State -->
        <div v-if="catalog.result.value.pending && catalog.view.value === 'grid'" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div v-for="i in 6" :key="i" class="animate-pulse">
            <USkeleton class="w-full h-72 rounded-lg" />
          </div>
        </div>

        <div v-else-if="catalog.result.value.pending && catalog.view.value === 'list'" class="space-y-4">
          <div v-for="i in 4" :key="i" class="animate-pulse">
            <USkeleton class="w-full h-32 rounded-lg" />
          </div>
        </div>

        <!-- Error State -->
        <UAlert v-else-if="catalog.result.value.error" color="error" variant="soft" title="Failed to load products"
          :description="(catalog.result.value.error as Error)?.message || 'Please try again later'" />

        <!-- Products Grid -->
        <div v-else-if="catalog.view.value === 'grid'"
          class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          <ProductCard v-for="p in catalog.result.value.products" :key="p.id" :product="p" />
        </div>

        <!-- Products List -->
        <div v-else class="space-y-4">
          <ProductCardList v-for="p in catalog.result.value.products" :key="p.id" :product="p" />
        </div>

        <!-- Empty State -->
        <UCard v-if="catalog.result.value.products.length === 0 && !catalog.result.value.pending" class="text-center py-12">
          <Icon name="i-heroicons-inbox" class="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 class="text-lg font-semibold text-gray-900 mb-2">{{ $t('No products found') }}</h3>
          <p class="text-gray-600 mb-4">{{ $t('Try adjusting your filters') }}</p>
          <UButton @click="catalog.reset" variant="outline">{{ $t('Clear filters') }}</UButton>
        </UCard>

        <UPagination v-if="catalog.result.value.products.length > 0" v-model:page="catalog.page.value" :items-per-page="catalog.perPage.value" :total.number="catalog.result.value.meta.total" />
      </UPageBody>
    </UPage>
</template>
<script setup lang="ts">
const { t: $t } = useI18n()

import FilterSidebar from '~/components/organisms/FilterSidebar.vue'
import ActiveFilters from '~/components/molecules/ActiveFilters.vue'
import SortSelect from '~/components/atoms/SortSelect.vue'
import ViewToggle from '~/components/molecules/ViewToggle.vue'
import ProductCardList from '~/components/molecules/ProductCardList.vue'
import ProductCard from '~/components/molecules/ProductCard.vue'
import { buildCategoryTree, buildCategoryPath } from '~/utils/catalog'
import type { ProductFilter, Category, Collection, SortOptions } from '~/types'
import StoreBreadcrumb from '../organisms/StoreBreadcrumb.vue'

const {
  pageTitle = 'Catalog',
  pageHeadline = '',
  pageDescription = '',
  category,
  collection,
  filter: catalogFilter = {},
  sortBy:   catalogSortBy,
  perPage:   catalogPerPage
} = defineProps<{
  pageTitle?: string,
  pageDescription?: string,
  pageHeadline?: string,
  collection?: Collection,
  filter?: ProductFilter,
  category?: Category
  sortBy?: SortOptions
  perPage?: number
}>()

const mobileFilters = ref<ProductFilter>({});

const catalog = await useCatalog({
  filter: {
    ...catalogFilter,
    categoryId: category?.id,
    collectionId: collection?.id
  },
  sortBy: catalogSortBy,
  perPage: catalogPerPage
});

const categoryStore = useCategoryStore();
const localePath = useLocalePath();

if (!categoryStore.loaded && !categoryStore.pending) {
  await categoryStore.load();
}

const catalogMinPrice = computed(() => Math.floor(catalog.result.value.meta.minPrice || 0));
const catalogMaxPrice = computed(() => Math.ceil(catalog.result.value.meta.maxPrice || 0));

const tree = computed(() => {
  if (category) {
    return buildCategoryTree(categoryStore.categories, category.id);
  }

  return buildCategoryTree(categoryStore.categories, null);
});

const breadcrumb = computed(() => {
  const items = [
    { label: $t('Home'), to: localePath({ name: 'index' }) },
    { label: $t('Catalog'), to: localePath({ name: 'catalog' }) }
  ];

  if (!category) {
    return items;
  }

  const path = buildCategoryPath(category.id, categoryStore.categories);

  return [
    ...items,
    ...path.map(c => ({
      label: c.name, to: localePath({ name: 'category-slug', params: { slug: c.slug } })
    }))
  ];
});

const catalogDescription = computed(() => {
  if (pageDescription) {
    return pageDescription;
  }
  return $t('Products available') + ': ' + catalog.result.value.meta.total;
});
</script>